import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MessageSquare, User, ShoppingCart, Bot, Activity } from 'lucide-react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';

import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TimelineContainer = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  height: 100%;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.error}40;
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const TimelineHeaderTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  max-height: 450px;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing[2]};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.error}40;
    border-radius: ${({ theme }) => theme.borders.radius.full};
    transition: background 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.error};
    }
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  position: relative;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-bottom: 1px solid ${({ theme }) => theme.isLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.04)'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)'};
  }
`;

const TimelineIcon = styled.div<{ $type: 'message' | 'lead' | 'sale' | 'bot' | 'system' }>`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $type }) => {
    switch ($type) {
      case 'message': return theme.isLight ? 'rgba(0, 122, 255, 0.1)' : 'rgba(0, 122, 255, 0.15)';
      case 'lead': return theme.isLight ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.15)';
      case 'sale': return theme.isLight ? 'rgba(255, 59, 48, 0.1)' : 'rgba(255, 59, 48, 0.15)';
      case 'bot': return theme.isLight ? 'rgba(255, 170, 0, 0.1)' : 'rgba(255, 170, 0, 0.15)';
      case 'system': return theme.isLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  color: ${({ theme, $type }) => {
    switch ($type) {
      case 'message': return theme.colors.info;
      case 'lead': return theme.colors.success;
      case 'sale': return theme.colors.error;
      case 'bot': return theme.colors.warning;
      case 'system': return theme.colors.textSecondary;
      default: return theme.colors.textPrimary;
    }
  }};
  border: 1px solid ${({ theme, $type }) => {
    const color = (() => {
      switch ($type) {
        case 'message': return theme.colors.info;
        case 'lead': return theme.colors.success;
        case 'sale': return theme.colors.error;
        case 'bot': return theme.colors.warning;
        default: return 'transparent';
      }
    })();
    return color !== 'transparent' ? `${color}33` : 'rgba(255, 255, 255, 0.1)';
  }};
  z-index: 1;
  position: relative;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  ${TimelineItem}:hover & {
    border-color: ${({ theme, $type }) => {
    switch ($type) {
      case 'message': return theme.colors.info;
      case 'lead': return theme.colors.success;
      case 'sale': return theme.colors.error;
      case 'bot': return theme.colors.warning;
      default: return theme.colors.textPrimary;
    }
  }}88;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  padding-top: ${({ theme }) => theme.spacing[1]};
`;

const TimelineItemTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
  letter-spacing: -0.01em;
  transition: color 0.2s;

  ${TimelineItem}:hover & {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const TimelineDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TimelineTime = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const TimelineBadge = styled.span<{ $variant: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-top: ${({ theme }) => theme.spacing[2]};
  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background: rgba(0, 255, 136, 0.1);
          color: ${theme.colors.success};
          border: 1px solid rgba(0, 255, 136, 0.2);
        `;
      case 'warning':
        return `
          background: rgba(255, 170, 0, 0.1);
          color: ${theme.colors.warning};
          border: 1px solid rgba(255, 170, 0, 0.2);
        `;
      case 'error':
        return `
          background: rgba(255, 68, 68, 0.1);
          color: ${theme.colors.error};
          border: 1px solid rgba(255, 68, 68, 0.2);
        `;
      case 'info':
        return `
          background: rgba(0, 170, 255, 0.1);
          color: ${theme.colors.info};
          border: 1px solid rgba(0, 170, 255, 0.2);
        `;
    }
  }}
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 420px;
  padding: ${({ theme }) => `${theme.spacing[12]} ${theme.spacing[4]}`};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borders.radius.full};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}15 0%, ${({ theme }) => theme.colors.primary}08 100%);
  border: 2px solid ${({ theme }) => theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${({ theme }) => theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${({ theme }) => theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  max-width: 380px;
  line-height: 1.6;
`;

const EmptyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  margin-top: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

interface ActivityItem {
  id: string;
  type: 'message' | 'lead' | 'sale' | 'bot' | 'system';
  title: string;
  description: string;
  timestamp: string;
  badge?: {
    text: string;
    variant: 'success' | 'warning' | 'error' | 'info';
  };
}

interface ActivityTimelineProps {
  activities: ActivityItem[];
  loading?: boolean;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ activities, loading = false }) => {
  const theme = useTheme();
  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'message': return MessageSquare;
      case 'lead': return User;
      case 'sale': return ShoppingCart;
      case 'bot': return Bot;
      case 'system': return Clock;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <TimelineContainer>
        <TimelineHeader>
          <TimelineHeaderTitle>Atividade Recente</TimelineHeaderTitle>
        </TimelineHeader>
        <TimelineList>
          {[...Array(5)].map((_, index) => (
            <TimelineItem key={index}>
              <TimelineIcon $type="system">
                <Clock size={16} />
              </TimelineIcon>
              <TimelineContent>
                <div style={{
                  height: '16px',
                  background: theme.colors.backgroundTertiary,
                  borderRadius: theme.borders.radius.md,
                  marginBottom: theme.spacing[1],
                  width: '60%'
                }} />
                <div style={{
                  height: '12px',
                  background: theme.colors.backgroundTertiary,
                  borderRadius: theme.borders.radius.md,
                  marginBottom: theme.spacing[2],
                  width: '80%'
                }} />
                <div style={{
                  height: '10px',
                  background: theme.colors.backgroundTertiary,
                  borderRadius: theme.borders.radius.md,
                  width: '40%'
                }} />
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineList>
      </TimelineContainer>
    );
  }

  if (activities.length === 0) {
    return (
      <TimelineContainer>
        <TimelineHeader>
          <TimelineHeaderTitle>Atividade Recente</TimelineHeaderTitle>
        </TimelineHeader>
        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            <Activity size={56} />
          </EmptyIcon>
          <EmptyTitle>Nenhuma atividade recente</EmptyTitle>
          <EmptyDescription>
            As interações com leads, vendas e mensagens do bot aparecerão aqui em tempo real.
          </EmptyDescription>
        </EmptyState>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      <TimelineHeader>
        <TimelineHeaderTitle>Atividade Recente</TimelineHeaderTitle>
      </TimelineHeader>

      <TimelineList>
        {activities.map((activity, index) => {
          const Icon = getIcon(activity.type);
          return (
            <TimelineItem
              key={activity.id}
              initial={{ opacity: 0, x: -30, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.08,
                ease: [0.34, 1.56, 0.64, 1]
              }}
            >
              <TimelineIcon $type={activity.type}>
                <Icon size={16} />
              </TimelineIcon>

              <TimelineContent>
                <TimelineItemTitle>{activity.title}</TimelineItemTitle>
                <TimelineDescription>
                  {activity.description.split('\n').map((msg, idx) => (
                    <React.Fragment key={idx}>
                      {msg}
                      <br />
                    </React.Fragment>
                  ))}
                </TimelineDescription>
                <TimelineTime>
                  <Clock size={12} />
                  <span>
                    {formatDistanceToNow(new Date(activity.timestamp), {
                      addSuffix: true,
                      locale: ptBR
                    })}
                  </span>
                </TimelineTime>
                {activity.badge && (
                  <TimelineBadge $variant={activity.badge.variant}>
                    {activity.badge.text}
                  </TimelineBadge>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </TimelineList>
    </TimelineContainer>
  );
};

export default ActivityTimeline;

